<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      <span class="subtitle-1 font-weight-medium">Status da Execução</span>

      <v-spacer></v-spacer>

      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        label="Pesquisar linha..."
        dense
        solo
        hide-details
        style="max-width: 250px;"
      ></v-text-field>
    </v-card-title>

    <v-divider></v-divider>

    <v-data-table
      :headers="headers"
      :items="listData"
      :search="search"
      :items-per-page="5"
      :loading="loading"
      class="elevation-0"
    >
      <template v-slot:header.retry="{ header }">
        <div class="d-flex align-center justify-center">
          <span>{{ header.text }}</span>

          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                class="ml-1"
                color="grey"
                v-on="on"
                v-bind="attrs"
              >
                mdi-information-outline
              </v-icon>
            </template>

            <span class="">
              Aqui você pode tenta novamente a execução para linhas que falharam. Ao clicar no botão, todas as linhas que estão disponíveis para essa opção, serão retentadas.
            </span>
          </v-tooltip>
        </div>
      </template>

      <!-- Coluna de status -->
      <template v-slot:item.status_opera="{ item }">
        <div class="d-flex align-center justify-center">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <span v-on="on" v-bind="attrs">
                <v-progress-circular
                  v-if="item.status_opera === 1"
                  indeterminate
                  size="22"
                  color="blue"
                ></v-progress-circular>

              <v-icon
                v-else-if="item.status_opera === 2"
                color="green"
                size="26"
              >mdi-check-circle</v-icon>

              <v-icon
                v-else-if="item.status_opera === 0"
                color="orange"
                size="26"
              >mdi-clock-outline</v-icon>

              <v-icon
                v-else
                color="red"
                size="26"
              >mdi-close-circle</v-icon>
              </span>
            </template>

            <span>{{ item.status_opera_name }}</span>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:item.status_join="{ item }">
        <div class="d-flex align-center justify-center">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <span v-on="on" v-bind="attrs">
                <v-progress-circular
                  v-if="item.status_join === 1"
                  indeterminate
                  size="22"
                  color="blue"
                ></v-progress-circular>

              <v-icon
                v-else-if="item.status_join === 2"
                color="green"
                size="26"
              >mdi-check-circle</v-icon>

              <v-icon
                v-else-if="item.status_join === 0"
                color="orange"
                size="26"
              >mdi-clock-outline</v-icon>

              <v-icon
                v-else
                color="red"
                size="26"
              >mdi-close-circle</v-icon>
              </span>
            </template>

            <span>{{ item.status_join_name }}</span>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:item.message="{ item }">
        <span v-if="item.message && (item.status_opera === 3 || item.status_opera === 4)" class="red--text font-weight-medium">
          {{ item.message }}
        </span>
        <span v-else-if="item.message">
          {{ item.message }}
        </span>
      </template>

      <!-- Coluna para retentar -->
      <template v-slot:item.retry="{ item }">
        <div class="d-flex justify-center">
          <v-tooltip top v-if="canShowRetryActions && item.status === 4">
            <template v-slot:activator="{ on, attrs }">
            <span v-on="on" v-bind="attrs">
              <v-btn
                small
                color="primary"
                :disabled="blockRetrie"
                :loading="loadingRetrie"
                @click="onRetryClick()"
              >
                <v-icon center small>mdi-reload</v-icon>
              </v-btn>
            </span>
            </template>

            <span>Tentar novamente</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
  props: {
    solicitation_id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      pollingInterval: null,
      search: "",
      baseHeaders: [
        { text: "Fornecedor", value: "status_opera", width: 80, sortable: false, align: "center" },
        { text: "Join", value: "status_join", width: 80, sortable: false, align: "center" },
        { text: "Linha", value: "nr_linha", align: "center" },
        { text: "Iniciado", value: "started_at_formatted", align: "center" },
        { text: "Finalizado", value: "finished_at_formatted", align: "center" },
        { text: "Mensagem", value: "message", sortable: false, align: "center" },
      ],
      listData: [],
      blockRetrie: false,
      loadingRetrie: false,
    };
  },
  async mounted() {
    await this.callLoadProcess();
    this.loading = false;

    this.pollingInterval = setInterval(() => {
      this.callLoadProcess();
    }, 5000);
  },
  computed: {
    ...mapState("interaction", ["process_suspend_line"]),

    headers() {
      const headers = [...this.baseHeaders];

      if (this.canShowRetryActions) {
        headers.push({
          text: "",
          value: "retry",
          align: "center",
          sortable: false,
          width: 120
        });
      }

      return headers;
    },

    hasProcessingLines() {
      return this.listData.some(item =>
        [0, 1].includes(item.status)
      );
    },

    hasRetryableLines() {
      return this.listData.some(item => item.status === 4);
    },

    canShowRetryActions() {
      return this.hasRetryableLines && !this.hasProcessingLines;
    }
  },
  methods: {
    ...mapActions("interaction", ["loadProcessSuspendLine"]),
    ...mapActions("solicitation", ["retryFinishSolicitation"]),

    async callLoadProcess() {
      try {
        await this.loadProcessSuspendLine({ solicitation_id: this.solicitation_id });
        this.listData = this.process_suspend_line;
      } catch (error) {
        await this.$swal("Oops...", error.message, "error");
      }
    },

    async onRetryClick() {
      this.blockRetrie = true;
      this.loadingRetrie = true;
      try {
        await this.retryFinishSolicitation({ solicitation_id: this.solicitation_id });
        this.callLoadProcess();
        await this.$swal("Sua solicitação será retentada pelo sistema.", "", "success");
      } catch (error) {
        await this.$swal("Oops...", error.message, "error");
      }
      this.blockRetrie = false;
      this.loadingRetrie = false;
    }
  },
  beforeDestroy() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }
};
</script>

<style scoped>
</style>